import { history, useAccess, useModel } from '@umijs/max';
import routes from '../../../config/routes';

// Định nghĩa cấu trúc route từ UmiJS
interface UmiRoute {
  path?: string;
  component?: string;
  access?: string;
  routes?: UmiRoute[];
  redirect?: string;
  layout?: boolean;
  [key: string]: any;
}

/**
 * Flatten routes và lọc các routes có component (bỏ qua redirect routes)
 * @param routes - Mảng routes từ UmiJS config
 * @param parentAccess - Permission của route cha (nếu có)
 * @returns Mảng các routes đã được flatten với path và access
 */
const flattenRoutes = (
  routes: UmiRoute[],
  parentAccess?: string,
): Array<{ path: string; access: string | undefined }> => {
  const result: Array<{ path: string; access: string | undefined }> = [];

  for (const route of routes) {
    // Bỏ qua các route không có path hoặc là redirect route
    if (!route.path || route.redirect || route.layout === false) {
      continue;
    }

    // Nếu route có component, thêm vào kết quả
    if (route.component) {
      result.push({
        path: route.path,
        access: route.access || parentAccess,
      });
    }

    // Nếu route có nested routes, đệ quy xử lý
    if (route.routes && route.routes.length > 0) {
      const nestedRoutes = flattenRoutes(
        route.routes,
        route.access || parentAccess,
      );
      result.push(...nestedRoutes);
    }
  }

  return result;
};

/**
 * Hook để tìm route được phép access đầu tiên
 */
export const useAccessibleRoute = () => {
  const access: any = useAccess();
  const { initialState } = useModel('@@initialState');

  const findFirstAccessibleRoute = (): string | null => {
    // Nếu là super admin, cho phép truy cập tất cả
    if (initialState?.user?.role === 'super_admin') {
      return '/dashboard/workplace';
    }

    // Flatten routes từ config
    const flattenedRoutes = flattenRoutes(routes as UmiRoute[]);

    // Tìm route đầu tiên mà user có quyền truy cập
    const accessibleRoute = flattenedRoutes.find((route) => {
      // Nếu route không có access requirement, bỏ qua
      if (!route.access) {
        return false;
      }

      // Kiểm tra quyền truy cập
      return access[route.access] === true;
    });

    console.log('accessibleRoute', accessibleRoute);

    if (accessibleRoute) {
      return accessibleRoute.path;
    }

    // Nếu không tìm thấy route nào, trả về null
    return null;
  };

  const redirectToFirstAccessibleRoute = () => {
    const accessibleRoute = findFirstAccessibleRoute();
    if (accessibleRoute && history.location.pathname !== accessibleRoute) {
      history.push(accessibleRoute);
    }
  };

  return {
    findFirstAccessibleRoute,
    redirectToFirstAccessibleRoute,
  };
};
